import {
  darkModeColors,
  ElementsEnvironmentContextProvider,
  ElementsUIContextProvider,
  FeatureFlagsContextProvider,
  LocalThemeContextProvider,
} from '@elements-financial/elements';
import { Outlet } from '@remix-run/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTypedLoaderData } from 'remix-typedjson';
import { RootDocument } from './components';
import { RootContextProvider } from './context';
import { rootLoader } from './loaders';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState } from 'react';
import { useChangeLanguage } from 'remix-i18next/react';

const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: true,
        // Disable cache for now until we fully migrate to react-query
        cacheTime: 0,
      },
    },
  });

export const RootPage = () => {
  const { locale, authentication, isDev, isInsiders, featureFlags, apiUrl } = useTypedLoaderData<typeof rootLoader>();

  // https://tanstack.com/query/v5/docs/framework/react/guides/ssr#initial-setup
  // The refresh token stays stable across access-token rotations. Keep this identity internal:
  // it must never become part of a query key or diagnostic output.
  const authenticationIdentity = authentication?.refreshToken || authentication?.token || null;
  const [queryClientState, setQueryClientState] = useState(() => ({
    authenticationIdentity,
    generation: 0,
    queryClient: createQueryClient(),
  }));
  const queryClientIsCurrent = queryClientState.authenticationIdentity === authenticationIdentity;

  if (!queryClientIsCurrent) {
    setQueryClientState({
      authenticationIdentity,
      generation: queryClientState.generation + 1,
      queryClient: createQueryClient(),
    });
  }

  const { generation: queryClientGeneration, queryClient } = queryClientState;
  useEffect(
    () => () => {
      queryClient.clear();
    },
    [queryClient]
  );

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files
  useChangeLanguage(locale);

  if (!queryClientIsCurrent) return null;

  return (
    <RootDocument>
      <ElementsEnvironmentContextProvider
        app="advisor"
        isDev={isDev}
        apiUrl={apiUrl}
        isInsiders={isInsiders}
        authentication={{
          accessToken: authentication?.token || '',
          refreshToken: authentication?.refreshToken || '',
        }}
      >
        <FeatureFlagsContextProvider {...featureFlags}>
          <ElementsUIContextProvider>
            <QueryClientProvider key={queryClientGeneration} client={queryClient}>
              <RootContextProvider isDev={isDev} authentication={authentication}>
                <LocalThemeContextProvider paletteSeedColor={darkModeColors.accent}>
                  <Outlet />
                </LocalThemeContextProvider>
              </RootContextProvider>
              <ReactQueryDevtools position="bottom-right" initialIsOpen={false} />
            </QueryClientProvider>
          </ElementsUIContextProvider>
        </FeatureFlagsContextProvider>
      </ElementsEnvironmentContextProvider>
    </RootDocument>
  );
};
